import React, { createContext, useContext, useEffect, useState } from 'react';
import { getAuth, onAuthStateChanged, createUserWithEmailAndPassword, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { getFirestore, doc, setDoc, getDoc, collection, addDoc, updateDoc, query, where, getDocs } from 'firebase/firestore';
import app from '../firebase/firebaseConfig';

const AuthContext = createContext();

export function useAuth() {
    return useContext(AuthContext);
}

export function AuthProvider({ children }) {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const auth = getAuth(app);
    const db = getFirestore(app);

    async function signup(email, password, farmDetails) {
        const { farmName, joinFarmId } = farmDetails;
        let finalFarmId;
        let userRole = 'worker'; // Default role for new users

        // Create the user account FIRST to get a UID
        const userCredential = await createUserWithEmailAndPassword(auth, email, password);
        const user = userCredential.user;

        if (joinFarmId) {
            // LOGIC TO JOIN A FARM
            const farmRef = doc(db, 'farms', joinFarmId);
            const farmSnap = await getDoc(farmRef);
            if (!farmSnap.exists()) {
                throw new Error("Farm ID not found. Please check the ID and try again.");
            }
            finalFarmId = joinFarmId;
            // Role remains 'worker' when joining
        } else {
            // LOGIC TO CREATE A FARM
            // The first user of a new farm becomes the admin
            userRole = 'admin'; 
            const farmsCollectionRef = collection(db, 'farms');
            const newFarmRef = await addDoc(farmsCollectionRef, {
                name: farmName,
                ownerId: user.uid,
                createdAt: new Date(),
            });
            finalFarmId = newFarmRef.id;
        }

        // Create the user's profile with their role
        const userRef = doc(db, "users", user.uid);
        await setDoc(userRef, {
            uid: user.uid,
            email: user.email,
            farmId: finalFarmId,
            role: userRole, // Set the user's role
            createdAt: new Date()
        });
        
        setCurrentUser({
            ...user,
            farmId: finalFarmId,
            role: userRole // Add role to the user object in state
        });

        return userCredential;
    }

    function login(email, password) {
        return signInWithEmailAndPassword(auth, email, password);
    }

    function logout() {
        return signOut(auth);
    }

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                const userRef = doc(db, "users", user.uid);
                const userSnap = await getDoc(userRef);
                
                if (userSnap.exists()) {
                    let userData = userSnap.data();
                    
                    // --- FIX: Check if a role exists, and if not, assign one ---
                    if (!userData.role) {
                        const farmRef = doc(db, 'farms', userData.farmId);
                        const farmSnap = await getDoc(farmRef);
                        
                        let assignedRole = 'worker'; // Default to worker
                        if (farmSnap.exists() && farmSnap.data().ownerId === user.uid) {
                            assignedRole = 'admin'; // Assign admin if they are the farm owner
                        }
                        
                        // Update the user document in Firestore with the new role
                        await updateDoc(userRef, { role: assignedRole });
                        
                        // Update the local userData object
                        userData.role = assignedRole;
                    }
                    
                    setCurrentUser({ ...user, farmId: userData.farmId, role: userData.role });
                } 
            } else {
                setCurrentUser(null);
            }
            setLoading(false);
        });
        return unsubscribe;
    }, [auth, db]);

    const value = {
        currentUser,
        signup,
        login,
        logout
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
}
